import { useQuery } from 'react-query';
import { UserType } from '../types/types';
import hash from 'object-hash';
import { NewUser } from '../components/login/NuevoUsuario';

export interface RegisterResponse extends UserType {
  register: boolean;
  message?: string;
}

const baseRoute = process.env.REACT_APP_BASE_URL;

async function usersApiCall(logedUser?: UserType | null) {
  if (!logedUser?.token) return null;
  const headers = {
    'Authorization': `Bearer ${logedUser.token}`,
  };
  const response = await fetch(
    `${baseRoute}/api/users`,
    { headers,  method: 'GET' },
  );
  if (!response.ok) {
    throw new Error('Problem fetching data');
  }
  const resp = await response.json();
  return resp as UserType[];
}

async function addOrEditUserApiCall(params?: UserType, logedUser?: UserType | null) {
  if (!logedUser?.token) return null;
  if (!params?.username) return null;

  delete params._id;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${logedUser.token}`,
  };
  const response = await fetch(
    `${baseRoute}/api/users`,
    { headers, body: JSON.stringify({ ...params, username: params.username.trim() }), method: 'POST' },
  );
  if (!response.ok) {
    throw new Error('Problem fetching data');
  }
  const resp = await response.json();
  return resp as UserType;
}

async function registerUser(params?: UserType) {
  if (!params?.username) return null;
  delete params._id;
  const headers = {
    'Content-Type': 'application/json',
  };
  const response = await fetch(
    `${baseRoute}/api/register`,
    { headers, body: JSON.stringify({ ...params, username: params.username.trim() }), method: 'POST' },
  );
  if (!response.ok) {
    throw new Error('Problem fetching data');
  }
  const resp = await response.json();
  return resp as RegisterResponse;
}

async function deleteUserApiCall(id?: string | null, logedUser?: UserType | null) {
  if (!logedUser?.token) return null;
  if (!id) return null;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${logedUser.token}`,
  };
  const response = await fetch(
    `${baseRoute}/api/users`,
    { headers, body: JSON.stringify({ id }), method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error('Problem fetching data');
  }
  const resp = await response.json();
  return resp as boolean;
}

export function useGetUsers(logedUser?: UserType | null) {
  return useQuery<UserType[] | null, Error>(
    ['getUsers', hash(logedUser ?? '')],
    () => usersApiCall(logedUser), {
      cacheTime: 3600,
      refetchOnWindowFocus: false,
    },
  );
}

export function useDeleteUser(id: string | null, logedUser?: UserType | null) {
  return useQuery<boolean | null, Error>(
    ['deleteUser', id, hash(logedUser ?? '')],
    () => deleteUserApiCall(id, logedUser), {
      cacheTime: 3600,
      refetchOnWindowFocus: false,
    },
  );
}


export function useAddOrUpdateUser(params?:UserType, logedUser?: UserType | null) {
  return useQuery<UserType | null, Error>(
    ['addUsers', hash(params || ''), hash(logedUser ?? '')],
    () => addOrEditUserApiCall(params, logedUser), {
      cacheTime: 3600,
      refetchOnWindowFocus: false,
      enabled: false,
      retry: false,
    },
  );
}

export function useRegister(params?:NewUser) {
  return useQuery<RegisterResponse | null, Error>(
    ['useRegister', hash(params || '')],
    () => registerUser(params), {
      cacheTime: 3600,
      refetchOnWindowFocus: false,
      enabled: false,
      retry: false,
    },
  );
}